import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Users, MessageSquare, Building2, CreditCard, Download, Loader2 } from "lucide-react";
import {
  VerifiersAdmin,
  BuildersAdmin,
  HardshipAdmin,
  StoriesAdmin,
  RoadmapAdmin,
  ImpactAdmin,
  ProjectsAdmin,
  BuilderApplicationsAdmin,
} from "@/components/AdminManage";
import AgentApplicationsAdmin from "@/components/AgentApplicationsAdmin";
import MarketingTemplatesAdmin from "@/components/MarketingTemplatesAdmin";
import { AgentLeaderboardAdmin, AuditLogAdmin } from "@/components/AgentLeaderboardAndAudit";
import { ReservationsAdmin, SiteVisitsAdmin } from "@/components/ReservationsAndVisitsAdmin";
import WaitingListAdmin from "@/components/WaitingListAdmin";
import SystemHealthTile from "@/components/SystemHealthTile";
import { toast } from "sonner";
import { Trash2, Search } from "lucide-react";

function money(v, cur = "GBP") {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: (cur || "GBP").toUpperCase(), maximumFractionDigits: 0 }).format(v || 0);
}

function csvEscape(s) {
  const v = s == null ? "" : String(s);
  if (v.includes(",") || v.includes('"') || v.includes("\n")) return `"${v.replaceAll('"','""')}"`;
  return v;
}

function downloadCSV(rows, headers, filename) {
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/** Dismissible regulatory to-do nudges for the admin. Dismissals persist in localStorage. */
function RegulatoryNudges() {
  const KEY = "mv_admin_nudge_hmrc_aml";
  const [hidden, setHidden] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return false;
      const rec = JSON.parse(raw);
      if (rec.status === "done") return true;
      // "later" snoozes for 7 days
      if (rec.status === "later" && Date.now() - rec.ts < 7 * 24 * 3600 * 1000) return true;
      return false;
    } catch { return false; }
  });
  const set = (status) => {
    localStorage.setItem(KEY, JSON.stringify({ status, ts: Date.now() }));
    setHidden(true);
    if (status === "done") toast.success("Marked as done — well done.");
  };
  if (hidden) return null;
  return (
    <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-5 flex flex-wrap gap-4 items-start" data-testid="admin-nudge-hmrc-aml">
      <div className="flex-1 min-w-[260px]">
        <p className="text-sm font-bold text-amber-900 mb-1">Regulatory to-do: HMRC Anti-Money Laundering registration</p>
        <p className="text-sm text-amber-800">
          Mavula Investment Ltd should be registered with HMRC for Money Laundering Supervision
          (Estate Agency Business) before handling customer property purchases. Apply at{" "}
          <a href="https://www.gov.uk/guidance/register-for-anti-money-laundering-supervision"
            target="_blank" rel="noopener noreferrer" className="underline font-semibold" data-testid="admin-nudge-hmrc-link">
            gov.uk — register for AML supervision
          </a>. Allow several weeks for HMRC to process it.
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button onClick={() => set("done")} data-testid="admin-nudge-hmrc-done"
          className="btn-green px-3 py-2 rounded-lg text-xs font-semibold transition-colors">Mark as done</button>
        <button onClick={() => set("later")} data-testid="admin-nudge-hmrc-later"
          className="px-3 py-2 rounded-lg text-xs font-semibold border border-amber-300 text-amber-900 hover:bg-amber-100 transition-colors">Remind me in 7 days</button>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, loading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [txns, setTxns] = useState([]);
  const [tab, setTab] = useState("leads");
  const [busy, setBusy] = useState(true);
  const [leadSearch, setLeadSearch] = useState("");
  const [selectedLeads, setSelectedLeads] = useState(new Set());

  useEffect(() => {
    if (loading || !user || user.role !== "admin") return;
    (async () => {
      try {
        const [s, l, t] = await Promise.all([
          api.get("/admin/summary"),
          api.get("/admin/leads"),
          api.get("/admin/transactions"),
        ]);
        setSummary(s.data);
        setLeads(l.data.leads || []);
        setTxns(t.data.transactions || []);
      } finally { setBusy(false); }
    })();
  }, [loading, user]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin mv-green-text" /></div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16" data-testid="admin-dashboard">
      <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Admin</span></div>
      <div className="mt-2 flex flex-wrap justify-between gap-4 items-end">
        <h1 className="font-display text-3xl md:text-4xl mv-green-text">MAVULA Control Room</h1>
        <Link to="/dashboard" className="btn-ghost">User dashboard →</Link>
        <Link to="/admin/ai-team" className="btn-gold px-4 py-2 rounded-lg text-sm font-semibold transition-colors" data-testid="admin-ai-team-link">✦ AI Team</Link>
      </div>

      {/* Regulatory nudges */}
      <RegulatoryNudges />

      {/* System Health */}
      <div className="mt-8">
        <SystemHealthTile />
      </div>

      {/* KPIs */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { i: <Users size={18}/>, k: "Members", v: summary?.users ?? "…" },
          { i: <MessageSquare size={18}/>, k: "Leads", v: summary?.leads ?? "…" },
          { i: <Building2 size={18}/>, k: "Projects", v: summary?.projects ?? "…" },
          { i: <CreditCard size={18}/>, k: "Paid Txns", v: summary?.transactions_paid ?? "…" },
          { i: <CreditCard size={18}/>, k: "Total Txns", v: summary?.transactions_total ?? "…" },
        ].map((c) => (
          <div key={c.k} className="mv-card p-5">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center mv-green-text mb-2" style={{ background: "rgba(212,175,55,0.16)" }}>{c.i}</div>
            <div className="text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>{c.k}</div>
            <div className="font-display text-2xl mv-green-text mt-1">{busy ? "…" : c.v}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mt-10 flex gap-2 border-b mv-hairline flex-wrap">
        {["leads","waiting-list","transactions","reservations","site-visits","projects","verifiers","builders","applications","agents","leaderboard","hardship","stories","roadmap","impact","marketing","audit"].map((tk) => (
          <button
            key={tk}
            onClick={() => setTab(tk)}
            className={`px-4 py-2.5 text-[14px] font-semibold transition-colors border-b-2 ${tab === tk ? "mv-green-text" : "text-neutral-500 border-transparent hover:mv-green-text"}`}
            style={tab === tk ? { borderColor: "var(--mv-gold)" } : {}}
            data-testid={`admin-tab-${tk}`}
          >{tk.charAt(0).toUpperCase() + tk.slice(1).replace(/-/g, " ")}</button>
        ))}
        <div className="ml-auto flex items-center">
          {(tab === "leads" || tab === "transactions") && (
            <button
              onClick={() => {
                if (tab === "leads") downloadCSV(leads, ["created_at","full_name","email","phone","country_current","country_invest","interest_type","message"], "mavula-leads.csv");
                else downloadCSV(txns, ["created_at","session_id","tx_ref","gateway","payment_type","amount","currency","status","payment_status","user_email"], "mavula-transactions.csv");
              }}
              className="btn-ghost !py-1.5 mb-1" data-testid="admin-download-csv"
            ><Download size={14}/> Download CSV</button>
          )}
        </div>
      </div>

      {/* Tables */}
      {tab === "leads" && (
        <div className="mt-4 mv-card overflow-hidden" data-testid="admin-leads-table">
          {/* Search + bulk actions toolbar */}
          <div className="flex flex-wrap items-center gap-2 p-3 border-b mv-hairline">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--mv-muted)" }}/>
              <input
                value={leadSearch}
                onChange={async (e) => {
                  const q = e.target.value;
                  setLeadSearch(q);
                  try {
                    const { data } = await api.get(`/admin/leads/search?q=${encodeURIComponent(q)}`);
                    setLeads(data.leads || []);
                  } catch {}
                }}
                placeholder="Search name / email / phone / country / ref code…"
                className="w-full pl-9 pr-3 py-2 rounded-xl border mv-hairline text-[13px]"
                data-testid="admin-leads-search"
              />
            </div>
            <button
              type="button"
              onClick={async () => {
                if (selectedLeads.size === 0) return;
                if (!window.confirm(`Delete ${selectedLeads.size} lead(s)?`)) return;
                try {
                  const { data } = await api.post("/admin/leads/bulk-delete", { ids: Array.from(selectedLeads) });
                  toast.success(`Deleted ${data.deleted} lead(s).`);
                  setLeads((xs) => xs.filter((l) => !selectedLeads.has(l.id)));
                  setSelectedLeads(new Set());
                } catch (e) {
                  toast.error("Bulk delete failed.");
                }
              }}
              disabled={selectedLeads.size === 0}
              className="btn-ghost !py-1.5"
              style={selectedLeads.size ? { color: "#8a2020" } : {}}
              data-testid="admin-leads-bulk-delete"
            >
              <Trash2 size={14}/> Delete selected ({selectedLeads.size})
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="text-left border-b mv-hairline bg-[var(--mv-bg)]" style={{ color: "var(--mv-muted)" }}>
                  <th className="py-3 px-4 font-medium w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all leads"
                      checked={leads.length > 0 && selectedLeads.size === leads.length}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedLeads(new Set(leads.map((l) => l.id)));
                        else setSelectedLeads(new Set());
                      }}
                      data-testid="admin-leads-select-all"
                    />
                  </th>
                  <th className="py-3 px-4 font-medium">Date</th>
                  <th className="py-3 px-4 font-medium">Name</th>
                  <th className="py-3 px-4 font-medium">Email</th>
                  <th className="py-3 px-4 font-medium">Phone</th>
                  <th className="py-3 px-4 font-medium">Living In</th>
                  <th className="py-3 px-4 font-medium">Invest In</th>
                  <th className="py-3 px-4 font-medium">Type</th>
                  <th className="py-3 px-4 font-medium">Ref</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b mv-hairline" data-testid={`admin-lead-row-${l.id}`}>
                    <td className="py-2.5 px-4">
                      <input
                        type="checkbox"
                        aria-label={`Select lead ${l.full_name}`}
                        checked={selectedLeads.has(l.id)}
                        onChange={(e) => {
                          setSelectedLeads((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(l.id); else next.delete(l.id);
                            return next;
                          });
                        }}
                        data-testid={`admin-lead-select-${l.id}`}
                      />
                    </td>
                    <td className="py-2.5 px-4 whitespace-nowrap">{(l.created_at || "").slice(0,10)}</td>
                    <td className="py-2.5 px-4 font-semibold mv-green-text">{l.full_name}</td>
                    <td className="py-2.5 px-4">{l.email}</td>
                    <td className="py-2.5 px-4">{l.phone}</td>
                    <td className="py-2.5 px-4">{l.country_current}</td>
                    <td className="py-2.5 px-4">{l.country_invest}</td>
                    <td className="py-2.5 px-4"><span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "rgba(212,175,55,0.16)", color: "#8a6b12" }}>{l.interest_type}</span></td>
                    <td className="py-2.5 px-4 font-mono text-[11.5px]">{l.ref_code || "—"}</td>
                  </tr>
                ))}
                {!leads.length && !busy && (
                  <tr><td colSpan={9} className="py-10 text-center text-neutral-500">No leads found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "transactions" && (
        <div className="mt-4 mv-card overflow-hidden" data-testid="admin-txns-table">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="text-left border-b mv-hairline bg-[var(--mv-bg)]" style={{ color: "var(--mv-muted)" }}>
                  <th className="py-3 px-4 font-medium">Date</th>
                  <th className="py-3 px-4 font-medium">Gateway</th>
                  <th className="py-3 px-4 font-medium">Type</th>
                  <th className="py-3 px-4 font-medium">User</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                  <th className="py-3 px-4 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-b mv-hairline">
                    <td className="py-2.5 px-4 whitespace-nowrap">{(t.created_at || "").slice(0,10)}</td>
                    <td className="py-2.5 px-4"><span className="text-[11px] px-2 py-0.5 rounded-full bg-black/5 mv-green-text">{t.gateway || "stripe"}</span></td>
                    <td className="py-2.5 px-4">{t.payment_type}</td>
                    <td className="py-2.5 px-4">{t.user_email || "guest"}</td>
                    <td className="py-2.5 px-4"><span className={`text-[11px] px-2 py-0.5 rounded-full`} style={t.payment_status === "paid" ? { background: "rgba(37,211,102,0.14)", color: "#0A7A2C" } : { background: "rgba(0,0,0,0.06)", color: "#4A5568" }}>{t.payment_status}</span></td>
                    <td className="py-2.5 px-4 text-right font-semibold">{money(t.amount, t.currency)}</td>
                  </tr>
                ))}
                {!txns.length && !busy && (
                  <tr><td colSpan={6} className="py-10 text-center text-neutral-500">No transactions yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "verifiers" && <div className="mt-4"><VerifiersAdmin /></div>}
      {tab === "projects"  && <div className="mt-4"><ProjectsAdmin /></div>}
      {tab === "builders"  && <div className="mt-4"><BuildersAdmin /></div>}
      {tab === "applications" && <div className="mt-4"><BuilderApplicationsAdmin /></div>}
      {tab === "agents" && <div className="mt-4"><AgentApplicationsAdmin /></div>}
      {tab === "hardship"  && <div className="mt-4"><HardshipAdmin /></div>}
      {tab === "stories"   && <div className="mt-4"><StoriesAdmin /></div>}
      {tab === "roadmap"   && <div className="mt-4"><RoadmapAdmin /></div>}
      {tab === "impact"    && <div className="mt-4"><ImpactAdmin /></div>}
      {tab === "marketing" && <div className="mt-4"><MarketingTemplatesAdmin /></div>}
      {tab === "leaderboard" && <div className="mt-4"><AgentLeaderboardAdmin /></div>}
      {tab === "audit" && <div className="mt-4"><AuditLogAdmin /></div>}
      {tab === "reservations" && <div className="mt-4"><ReservationsAdmin /></div>}
      {tab === "site-visits" && <div className="mt-4"><SiteVisitsAdmin /></div>}
      {tab === "waiting-list" && <WaitingListAdmin />}
    </div>
  );
}
